import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Fade,
  Alert,
  useTheme,
  alpha,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  LinearProgress,
  Tooltip,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Security,
  Login as LoginIcon,
  Business,
  Person,
  Shield,
  Speed,
  HelpOutline,
  KeyboardCapslock,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { login, logout } from '../../../Features/Authentication/authSlice';
import * as tokenStorage from '../../../shared/utils/tokenStorage';
import LegalModal from './LegalModal';
import ConsentBanner from './ConsentBanner';

// Unified theme - Professional Blue/Teal gradient
const unifiedTheme = {
  primary: '#0A2463',
  secondary: '#3E92CC',
  accent: '#2EC4B6',
  dark: '#001845',
  light: '#D8E2DC',
  gradient1: 'linear-gradient(135deg, #001845 0%, #0A2463 25%, #3E92CC 75%, #2EC4B6 100%)',
  gradient2: 'radial-gradient(ellipse at top left, rgba(46, 196, 182, 0.3) 0%, transparent 50%)',
  gradient3:
    'radial-gradient(ellipse at bottom right, rgba(62, 146, 204, 0.3) 0%, transparent 50%)',
};

// Password strength calculator
const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  if (score <= 1) return { score: 20, label: 'Weak', color: '#f44336' };
  if (score === 2) return { score: 40, label: 'Fair', color: '#ff9800' };
  if (score === 3) return { score: 60, label: 'Good', color: '#ffc107' };
  if (score === 4) return { score: 80, label: 'Strong', color: '#4caf50' };
  return { score: 100, label: 'Very Strong', color: '#2e7d32' };
};

const UnifiedLogin = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const mousePosition = useRef({ x: 0, y: 0 });
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // API Base URL for backend calls - matches authInstance baseURL from api.js
  const API_BASE_URL = process.env.REACT_APP_AUTH_URL || 'http://localhost:8000/api/v1/rbac';

  const { isLoading, error: authError, user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [animateElements, setAnimateElements] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [rememberMe, setRememberMe] = useState(tokenStorage.isRememberMe());

  // Temporary password change state
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const [newPasswordData, setNewPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  // Forgot password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Caps Lock detection
  const handleKeyEvent = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };

  // Particle animation system
  useEffect(() => {
    setAnimateElements(true);

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 50;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 1;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;

        // React to mouse
        const dx = mousePosition.current.x - this.x;
        const dy = mousePosition.current.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 100) {
          this.x -= dx * 0.01;
          this.y -= dy * 0.01;
        }
      }

      draw() {
        ctx.fillStyle = `rgba(62, 146, 204, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Connect particles with lines
    const connectParticles = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.strokeStyle = `rgba(46, 196, 182, ${0.2 * (1 - distance / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      connectParticles();
      requestAnimationFrame(animate);
    };

    animate();

    // Mouse tracking
    const handleMouseMove = (e) => {
      mousePosition.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Handle email links and existing login state
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromEmail = urlParams.get('from');

    // If user came from email, clear any existing tokens to force fresh login
    if (fromEmail === 'email') {
      console.log('User came from email - clearing existing tokens');
      tokenStorage.removeItem('token');
      tokenStorage.removeItem('refreshToken');
      // Also clear localStorage for legacy cleanup
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      dispatch(logout());
      return; // Don't auto-redirect
    }

    // Normal auto-redirect logic for existing users
    if (user && user.id) {
      // Don't auto-redirect super admins from the regular login page
      // They should use the dedicated super admin login
      if (user.max_role_level >= 3) {
        // Clear auth state for super admin users accessing regular login
        dispatch(logout());
        setError('Please use the Super Admin login page');
        return;
      }
      const redirect = location.state?.from?.pathname || determineUserPortal(user);
      navigate(redirect, { replace: true });
    }
  }, [user, navigate, dispatch, location.search, location.state]);

  // Determine portal based on user roles and organization
  const determineUserPortal = (user) => {
    if (!user) return '/login';

    console.log('Determining portal for user:', {
      username: user.username,
      roles: user.roles,
      max_role_level: user.max_role_level,
      organizations: user.organizations,
      current_organization: user.current_organization,
    });

    // Check for explicit portal assignment
    if (user.assigned_portal) {
      console.log('Using assigned portal:', user.assigned_portal);
      // Map portal names to routes
      const portalRoutes = {
        'super-admin': '/super-admin',
        admin: '/admin',
        bureau: '/bureau',
        client: '/client',
        technician: '/technician',
        sales: '/sales',
      };
      return portalRoutes[user.assigned_portal] || '/client';
    }

    // First check RBAC max_role_level for super admin
    if (user.max_role_level >= 3) {
      console.log('User has Super Admin level (3), redirecting to /super-admin');
      return '/super-admin';
    }

    // Get RBAC role names
    let roleNames = [];
    if (user.roles && user.roles.length > 0) {
      roleNames = user.roles.map((role) => (typeof role === 'string' ? role : role.name));
      console.log('User RBAC roles:', roleNames);
    }

    // If no RBAC roles, fall back to Django groups (legacy)
    if (roleNames.length === 0 && user.groups && user.groups.length > 0) {
      roleNames = user.groups.map((group) => (typeof group === 'string' ? group : group.name));
      console.log('Using legacy Django groups:', roleNames);
    }

    // Priority order for portal assignment based on role names
    const rolePriority = [
      { role: 'Super Admin', portal: '/super-admin' },
      { role: 'Admin', portal: '/admin' },
      { role: 'Administrator', portal: '/admin' },
      { role: 'Manager', portal: '/admin' },
      { role: 'Bureau Supervisor', portal: '/bureau' },
      { role: 'Bureau Agent', portal: '/bureau' },
      { role: 'Bureau Agents Staff', portal: '/bureau' },
      { role: 'Supervisor', portal: '/bureau' },
      { role: 'Client Manager', portal: '/client' },
      { role: 'Client User', portal: '/client' },
      { role: 'Client', portal: '/client' },
      { role: 'Viewer', portal: '/client' },
      { role: 'User', portal: '/client' },
    ];

    // Check each role in priority order
    for (const { role, portal } of rolePriority) {
      if (roleNames.includes(role)) {
        console.log(`Found matching role: ${role}, redirecting to ${portal}`);
        return portal;
      }
    }

    // Fallback: Check organization type
    if (user.current_organization) {
      const orgType = user.current_organization.type;
      console.log('Using current organization type fallback:', orgType);
      if (orgType === 'fleetcam') {
        return '/bureau'; // Default FleetCam employees to bureau
      } else if (orgType === 'client') {
        return '/client'; // Default client organizations to client portal
      }
    } else if (user.organizations && user.organizations.length > 0) {
      const orgType = user.organizations[0].type;
      console.log('Using first organization type fallback:', orgType);
      if (orgType === 'fleetcam') {
        return '/bureau';
      } else if (orgType === 'client') {
        return '/client';
      }
    }

    // Legacy fallback for older data structure
    if (user.organization?.type === 'CLIENT') {
      return '/client';
    }

    if (user.organization?.name === 'FleetCam') {
      return '/bureau';
    }

    // Default to client portal (safest option)
    console.log('No specific portal found, defaulting to /client');
    return '/client';
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    setError('');
  };

  const handleNewPasswordChange = (field) => (event) => {
    setNewPasswordData({ ...newPasswordData, [field]: event.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const username = formData.username.trim();
    const password = formData.password;

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    if (username.length > 150) {
      setError('Username is too long');
      return;
    }

    if (password.length > 500) {
      setError('Password is too long');
      return;
    }

    setLoading(true);

    try {
      // Try direct API call to handle temporary password response
      const response = await fetch(
        `${API_BASE_URL}${API_BASE_URL.endsWith('/') ? '' : '/'}auth/login/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: username,
            password: password,
          }),
        }
      );

      const data = await response.json();

      if (response.status === 202 && data.requires_password_change) {
        // Temporary password detected
        setRequiresPasswordChange(true);
        setTempUser(data.user);
        setError('');
        setLoading(false);
        return;
      }

      if (response.ok) {
        // Normal successful login - use Redux action
        const result = await dispatch(
          login({
            username: username,
            password: password,
            rememberMe,
          })
        ).unwrap();

        if (result.user) {
          const portal = determineUserPortal(result.user);
          const from = location.state?.from?.pathname || portal;
          navigate(from, { replace: true });
        }
      } else {
        // Use backend message if available, otherwise a friendly generic message
        const backendError = data.error || data.detail;
        if (backendError && backendError.toLowerCase().includes('locked')) {
          setError(backendError);
        } else if (backendError && backendError.toLowerCase().includes('disabled')) {
          setError('This account has been deactivated. Please contact your administrator.');
        } else {
          setError('Incorrect username or password. Please check your credentials and try again.');
        }
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to the server. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPasswordData.newPassword || !newPasswordData.confirmPassword) {
      setError('Please enter both password fields');
      return;
    }

    if (newPasswordData.newPassword !== newPasswordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPasswordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}${API_BASE_URL.endsWith('/') ? '' : '/'}auth/change-temporary-password/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: tempUser.username,
            current_password: formData.password,
            new_password: newPasswordData.newPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Password changed successfully, tokens provided
        // Store tokens and redirect
        if (data.access && data.refresh) {
          tokenStorage.setToken(data.access);
          tokenStorage.setRefreshToken(data.refresh);

          // Determine portal from user data
          const portal = determineUserPortal(data.user);
          navigate(portal, { replace: true });
        }
      } else {
        setError(data.error || 'Failed to change password. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Password change error:', err);
      setError('Failed to change password. Please try again.');
      setLoading(false);
    }
  };

  const handleModal = (content) => {
    setModalContent(content);
    setModalOpen(true);
  };

  const handleForgotPassword = async () => {
    if (!forgotUsername.trim() || !forgotEmail.trim()) {
      setForgotError('Please enter both your username and email address.');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const response = await fetch(`${API_BASE_URL}${API_BASE_URL.endsWith('/') ? '' : '/'}auth/forgot-password/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim(), username: forgotUsername.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        setForgotOpen(false);
        setSnackbar({
          open: true,
          message: `Password reset link sent to ${data.masked_email || forgotEmail}`,
          severity: 'success',
        });
      } else {
        setForgotError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setForgotError('Unable to connect to the server. Please try again later.');
    } finally {
      setForgotLoading(false);
    }
  };

  const textFieldStyles = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: '#1a1f2e',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: alpha('#ffffff', 0.1),
        transition: 'all 0.3s ease',
      },
      '&:hover': {
        backgroundColor: '#1e2436',
        '& fieldset': {
          borderColor: alpha('#ffffff', 0.2),
        },
      },
      '&.Mui-focused': {
        backgroundColor: '#1e2436',
        '& fieldset': {
          borderColor: unifiedTheme.accent,
          borderWidth: 2,
        },
        boxShadow: `
          0 0 0 3px ${alpha(unifiedTheme.accent, 0.12)},
          0 0 20px ${alpha(unifiedTheme.secondary, 0.15)},
          inset 0 0 0 0 transparent
        `,
      },
      '& input': {
        color: '#fff',
        borderRadius: 'inherit',
        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
          {
            WebkitBoxShadow: '0 0 0 100px #1a1f2e inset !important',
            WebkitTextFillColor: '#fff !important',
            caretColor: '#fff',
            borderRadius: 'inherit',
            transition: 'background-color 5000s ease-in-out 0s',
          },
      },
      '& input::placeholder': {
        color: alpha('#ffffff', 0.4),
        opacity: 1,
      },
    },
    '& .MuiInputLabel-root': {
      display: 'none',
    },
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          background: unifiedTheme.gradient1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Particle Canvas */}
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        />

        {/* Animated Gradient Overlays */}
        <Box
          sx={{
            position: 'absolute',
            width: '200%',
            height: '200%',
            background: unifiedTheme.gradient2,
            animation: 'rotate 30s linear infinite',
            opacity: 0.6,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '200%',
            height: '200%',
            background: unifiedTheme.gradient3,
            animation: 'rotate 40s linear infinite reverse',
            opacity: 0.4,
          }}
        />

        {/* Floating Icons - hidden on mobile */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            display: isMobile ? 'none' : 'block',
          }}
        >
          <Fade in={animateElements} timeout={1000}>
            <Shield
              sx={{
                position: 'absolute',
                top: '10%',
                left: '5%',
                fontSize: 100,
                color: alpha(unifiedTheme.accent, 0.1),
                animation: 'float 6s ease-in-out infinite',
              }}
            />
          </Fade>
          <Fade in={animateElements} timeout={1500}>
            <Business
              sx={{
                position: 'absolute',
                top: '20%',
                right: '10%',
                fontSize: 80,
                color: alpha(unifiedTheme.secondary, 0.1),
                animation: 'float 8s ease-in-out infinite',
                animationDelay: '1s',
              }}
            />
          </Fade>
          <Fade in={animateElements} timeout={2000}>
            <Speed
              sx={{
                position: 'absolute',
                bottom: '15%',
                left: '10%',
                fontSize: 90,
                color: alpha(unifiedTheme.primary, 0.1),
                animation: 'float 7s ease-in-out infinite',
                animationDelay: '2s',
              }}
            />
          </Fade>
        </Box>

        {/* Login Card */}
        <Fade in={animateElements} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              width: '100%',
              maxWidth: 480,
              mx: isMobile ? 2 : 0,
              borderRadius: isMobile ? 3 : 4,
              overflow: 'hidden',
              position: 'relative',
              backgroundColor: '#141820',
              border: `1px solid ${alpha('#ffffff', 0.08)}`,
              boxShadow: `
              0 24px 80px ${alpha('#000', 0.5)},
              0 8px 32px ${alpha('#000', 0.3)}
            `,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '200px',
                background: `linear-gradient(180deg, ${alpha(unifiedTheme.primary, 0.08)} 0%, transparent 100%)`,
                pointerEvents: 'none',
              },
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${unifiedTheme.dark} 0%, ${unifiedTheme.primary} 100%)`,
                p: isMobile ? 3 : 4,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                borderBottom: '3px solid',
                borderImage: `linear-gradient(90deg, ${unifiedTheme.secondary}, ${unifiedTheme.accent}) 1`,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                  opacity: 0.4,
                }}
              />
              {/* FleetCam Logo */}
              <Box sx={{ mb: 2 }}>
                <img
                  src="/images/FleetCam - Logo.png"
                  alt="FleetCam"
                  style={{
                    height: isMobile ? 44 : 60,
                    opacity: 0.95,
                  }}
                />
              </Box>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{
                  fontWeight: 800,
                  color: 'white',
                  mb: 1,
                  letterSpacing: '-0.02em',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                Welcome to FleetCam
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha(unifiedTheme.light, 0.9),
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                Fleet Management & Monitoring System
              </Typography>
            </Box>

            {/* Form Section */}
            <Box sx={{ p: isMobile ? 3 : 5 }}>
              {/* Intro text */}
              <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  color: alpha('#ffffff', 0.6),
                  mb: 3,
                  fontWeight: 400,
                }}
              >
                Sign in to your account
              </Typography>
              {/* Error Alert */}
              {(error || authError) && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    backgroundColor: alpha(theme.palette.error.main, 0.15),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    color: '#fff',
                    '& .MuiAlert-icon': { color: theme.palette.error.light },
                  }}
                >
                  {error || authError}
                </Alert>
              )}

              {/* Conditional Form Rendering */}
              {requiresPasswordChange ? (
                // Password Change Form
                <form onSubmit={handlePasswordChange}>
                  <Alert
                    severity="warning"
                    sx={{
                      mb: 3,
                      backgroundColor: alpha(theme.palette.warning.main, 0.15),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                      color: '#fff',
                      '& .MuiAlert-icon': { color: theme.palette.warning.light },
                    }}
                  >
                    Welcome {tempUser?.first_name}! You must change your temporary password to
                    continue.
                  </Alert>

                  <TextField
                    fullWidth
                    placeholder="New Password"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPasswordData.newPassword}
                    onChange={handleNewPasswordChange('newPassword')}
                    onKeyUp={handleKeyEvent}
                    onKeyDown={handleKeyEvent}
                    disabled={loading}
                    sx={{ ...textFieldStyles, mb: 0.5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Security sx={{ color: unifiedTheme.secondary }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {capsLockOn && (
                            <Tooltip title="Caps Lock is on">
                              <KeyboardCapslock sx={{ color: '#ff9800', mr: 0.5, fontSize: 20 }} />
                            </Tooltip>
                          )}
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={loading}
                            sx={{
                              color: unifiedTheme.secondary,
                              '&:hover': {
                                backgroundColor: alpha(unifiedTheme.secondary, 0.1),
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="new-password"
                    autoFocus
                  />

                  {/* Password Strength Indicator */}
                  {newPasswordData.newPassword && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={getPasswordStrength(newPasswordData.newPassword).score}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.grey[300], 0.3),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 2,
                            backgroundColor: getPasswordStrength(newPasswordData.newPassword).color,
                            transition: 'all 0.3s ease',
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: getPasswordStrength(newPasswordData.newPassword).color,
                          fontWeight: 500,
                          mt: 0.5,
                          display: 'block',
                        }}
                      >
                        {getPasswordStrength(newPasswordData.newPassword).label}
                      </Typography>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    placeholder="Confirm New Password"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPasswordData.confirmPassword}
                    onChange={handleNewPasswordChange('confirmPassword')}
                    onKeyUp={handleKeyEvent}
                    onKeyDown={handleKeyEvent}
                    disabled={loading}
                    sx={textFieldStyles}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Security sx={{ color: unifiedTheme.secondary }} />
                        </InputAdornment>
                      ),
                      endAdornment: capsLockOn ? (
                        <InputAdornment position="end">
                          <Tooltip title="Caps Lock is on">
                            <KeyboardCapslock sx={{ color: '#ff9800', fontSize: 20 }} />
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                    autoComplete="new-password"
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Security />}
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      letterSpacing: '0.02em',
                      background: `linear-gradient(135deg, ${unifiedTheme.secondary} 0%, ${unifiedTheme.accent} 100%)`,
                      boxShadow: `0 4px 20px ${alpha(unifiedTheme.secondary, 0.3)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 30px ${alpha(unifiedTheme.secondary, 0.4)}`,
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&.Mui-disabled': {
                        background: alpha(theme.palette.action.disabledBackground, 0.3),
                      },
                    }}
                  >
                    {loading ? 'Changing Password...' : 'Change Password'}
                  </Button>
                </form>
              ) : (
                // Login Form
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    placeholder="Username or Email"
                    name="username"
                    value={formData.username}
                    onChange={handleChange('username')}
                    disabled={loading || isLoading}
                    sx={textFieldStyles}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: unifiedTheme.secondary }} />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="username"
                    autoFocus
                  />

                  <TextField
                    fullWidth
                    placeholder="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange('password')}
                    onKeyUp={handleKeyEvent}
                    onKeyDown={handleKeyEvent}
                    disabled={loading || isLoading}
                    sx={textFieldStyles}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Security sx={{ color: unifiedTheme.secondary }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {capsLockOn && (
                            <Tooltip title="Caps Lock is on">
                              <KeyboardCapslock sx={{ color: '#ff9800', mr: 0.5, fontSize: 20 }} />
                            </Tooltip>
                          )}
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={loading || isLoading}
                            sx={{
                              color: unifiedTheme.secondary,
                              '&:hover': {
                                backgroundColor: alpha(unifiedTheme.secondary, 0.1),
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="current-password"
                  />

                  {/* Remember Me & Forgot Password */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          size="small"
                          sx={{
                            color: alpha(unifiedTheme.secondary, 0.5),
                            '&.Mui-checked': {
                              color: unifiedTheme.accent,
                            },
                          }}
                        />
                      }
                      label="Remember me"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.85rem',
                          color: alpha('#ffffff', 0.6),
                        },
                      }}
                    />
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => {
                        setForgotUsername(formData.username);
                        setForgotEmail('');
                        setForgotError('');
                        setForgotOpen(true);
                      }}
                      sx={{
                        color: unifiedTheme.accent,
                        textDecoration: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  {/* Submit Button */}
                  <Box sx={{ position: 'relative' }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading || isLoading}
                      startIcon={!loading && !isLoading ? <LoginIcon /> : null}
                      sx={{
                        py: 1.5,
                        borderRadius: 3,
                        fontSize: '1.05rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        letterSpacing: '0.02em',
                        background:
                          loading || isLoading
                            ? `linear-gradient(135deg, ${alpha(unifiedTheme.secondary, 0.7)} 0%, ${alpha(unifiedTheme.accent, 0.7)} 100%)`
                            : `linear-gradient(135deg, ${unifiedTheme.secondary} 0%, ${unifiedTheme.accent} 100%)`,
                        color: 'white',
                        boxShadow: `0 4px 20px ${alpha(unifiedTheme.secondary, 0.3)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: loading || isLoading ? 'none' : 'translateY(-2px)',
                          boxShadow: `0 8px 30px ${alpha(unifiedTheme.secondary, 0.4)}`,
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                        '&.Mui-disabled': {
                          color: 'white',
                        },
                      }}
                    >
                      {loading || isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                    {(loading || isLoading) && (
                      <LinearProgress
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          borderRadius: '0 0 4px 4px',
                          backgroundColor: alpha(unifiedTheme.accent, 0.2),
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: unifiedTheme.accent,
                          },
                        }}
                      />
                    )}
                  </Box>
                </form>
              )}

              {/* Footer Links - Only show for login form */}
              {!requiresPasswordChange && (
                <>
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.5) }}>
                      By signing in, you agree to our{' '}
                      <Link
                        component="button"
                        type="button"
                        variant="caption"
                        onClick={() => handleModal('terms')}
                        sx={{
                          color: unifiedTheme.accent,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Terms of Service
                      </Link>
                      {' and '}
                      <Link
                        component="button"
                        type="button"
                        variant="caption"
                        onClick={() => handleModal('privacy')}
                        sx={{
                          color: unifiedTheme.accent,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Privacy Policy
                      </Link>
                    </Typography>
                  </Box>

                  {/* Support Link */}
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha('#ffffff', 0.4),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                      }}
                    >
                      <HelpOutline sx={{ fontSize: 14 }} />
                      Need help?{' '}
                      <Link
                        href="mailto:systems@fleetcamsa.com"
                        variant="caption"
                        sx={{
                          color: unifiedTheme.secondary,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Contact support
                      </Link>
                      {' or call '}
                      <Link
                        href="tel:0800008743"
                        variant="caption"
                        sx={{
                          color: unifiedTheme.secondary,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        080 0008 743
                      </Link>
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Fade>

        {/* Copyright Footer - Outside the card */}
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: isMobile ? 12 : 20,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: alpha('#fff', 0.4),
            fontSize: '0.7rem',
          }}
        >
          &copy; {new Date().getFullYear()} FleetCam Pty (Ltd). All rights reserved.
        </Typography>
      </Box>

      {/* Legal Modal */}
      <LegalModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        contentType={modalContent}
        theme={unifiedTheme}
      />

      {/* Consent Banner */}
      <ConsentBanner
        theme={unifiedTheme}
        onAccept={() => console.log('Consent accepted')}
        onDecline={() => console.log('Consent declined')}
        onViewPreferences={() => handleModal('privacy')}
      />

      {/* Keyframe Animations */}
      <style jsx="true">{`
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0) rotate(0deg);
          }
          25% {
            transform: translateY(-20px) rotate(5deg);
          }
          75% {
            transform: translateY(20px) rotate(-5deg);
          }
        }
      `}</style>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotOpen}
        onClose={() => !forgotLoading && setForgotOpen(false)}
        maxWidth={false}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(8px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            backgroundColor: '#141820',
            border: `1px solid ${alpha('#ffffff', 0.08)}`,
            overflow: 'hidden',
            width: 480,
            maxWidth: '90vw',
          },
        }}
      >
        {/* Dialog Header with gradient accent */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '2px solid transparent',
            borderImage: `linear-gradient(90deg, ${unifiedTheme.secondary}, ${unifiedTheme.accent}) 1`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#fff',
              mb: 0.5,
            }}
          >
            Reset Password
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: alpha('#ffffff', 0.5) }}
          >
            Enter your username and email address and we'll send you a
            link to reset your password.
          </Typography>
        </Box>

        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          {forgotError && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                backgroundColor: alpha('#f44336', 0.1),
                color: '#fff',
                border: `1px solid ${alpha('#f44336', 0.3)}`,
                '& .MuiAlert-icon': { color: '#f44336' },
              }}
            >
              {forgotError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            placeholder="Username"
            value={forgotUsername}
            onChange={(e) => setForgotUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && document.getElementById('forgot-email-field')?.focus()}
            disabled={forgotLoading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#1a1f2e',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& fieldset': {
                  borderColor: alpha('#ffffff', 0.1),
                  transition: 'all 0.3s ease',
                },
                '&:hover': {
                  backgroundColor: '#1e2436',
                  '& fieldset': {
                    borderColor: alpha('#ffffff', 0.2),
                  },
                },
                '&.Mui-focused': {
                  backgroundColor: '#1e2436',
                  '& fieldset': {
                    borderColor: unifiedTheme.accent,
                    borderWidth: 2,
                  },
                  boxShadow: `
                    0 0 0 3px ${alpha(unifiedTheme.accent, 0.12)},
                    0 0 20px ${alpha(unifiedTheme.secondary, 0.15)}
                  `,
                },
                '& input': {
                  color: '#fff',
                  borderRadius: 'inherit',
                  '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
                    {
                      WebkitBoxShadow:
                        '0 0 0 100px #1a1f2e inset !important',
                      WebkitTextFillColor: '#fff !important',
                      caretColor: '#fff',
                      borderRadius: 'inherit',
                      transition:
                        'background-color 5000s ease-in-out 0s',
                    },
                },
                '& input::placeholder': {
                  color: alpha('#ffffff', 0.4),
                  opacity: 1,
                },
              },
              '& .MuiInputLabel-root': { display: 'none' },
            }}
          />
          <TextField
            fullWidth
            id="forgot-email-field"
            placeholder="Email Address"
            type="email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
            disabled={forgotLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#1a1f2e',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& fieldset': {
                  borderColor: alpha('#ffffff', 0.1),
                  transition: 'all 0.3s ease',
                },
                '&:hover': {
                  backgroundColor: '#1e2436',
                  '& fieldset': {
                    borderColor: alpha('#ffffff', 0.2),
                  },
                },
                '&.Mui-focused': {
                  backgroundColor: '#1e2436',
                  '& fieldset': {
                    borderColor: unifiedTheme.accent,
                    borderWidth: 2,
                  },
                  boxShadow: `
                    0 0 0 3px ${alpha(unifiedTheme.accent, 0.12)},
                    0 0 20px ${alpha(unifiedTheme.secondary, 0.15)}
                  `,
                },
                '& input': {
                  color: '#fff',
                  borderRadius: 'inherit',
                  '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
                    {
                      WebkitBoxShadow:
                        '0 0 0 100px #1a1f2e inset !important',
                      WebkitTextFillColor: '#fff !important',
                      caretColor: '#fff',
                      borderRadius: 'inherit',
                      transition:
                        'background-color 5000s ease-in-out 0s',
                    },
                },
                '& input::placeholder': {
                  color: alpha('#ffffff', 0.4),
                  opacity: 1,
                },
              },
              '& .MuiInputLabel-root': { display: 'none' },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1.5 }}>
          <Button
            onClick={() => setForgotOpen(false)}
            disabled={forgotLoading}
            sx={{
              color: alpha('#ffffff', 0.5),
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 3,
              px: 2.5,
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.05),
                color: alpha('#ffffff', 0.8),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleForgotPassword}
            variant="contained"
            disabled={forgotLoading}
            startIcon={
              forgotLoading ? (
                <CircularProgress size={16} sx={{ color: '#fff' }} />
              ) : null
            }
            sx={{
              background: `linear-gradient(135deg, ${unifiedTheme.secondary} 0%, ${unifiedTheme.accent} 100%)`,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 3,
              px: 3,
              py: 1,
              boxShadow: `0 4px 15px ${alpha(unifiedTheme.accent, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${unifiedTheme.secondary} 20%, ${unifiedTheme.accent} 100%)`,
                boxShadow: `0 6px 20px ${alpha(unifiedTheme.accent, 0.4)}`,
              },
              '&.Mui-disabled': {
                background: alpha('#ffffff', 0.1),
                color: alpha('#ffffff', 0.3),
              },
            }}
          >
            {forgotLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UnifiedLogin;
